import React, { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import dayjs from "dayjs";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Input from "@mui/material/Input";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import InputLabel from "@mui/material/InputLabel";
import FormControl from "@mui/material/FormControl";
import InputAdornment from "@mui/material/InputAdornment";
import SearchIcon from "@mui/icons-material/Search";
import Table from "@mui/material/Table";
import TableRow from "@mui/material/TableRow";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableContainer from "@mui/material/TableContainer";
import CircularProgress from "@mui/material/CircularProgress";
import * as api from "../../../shared/lib/api";
import { exportResponseClassifications } from "../../../features/product/services/response-classification";
import { Paginator } from "../../../shared/components/paginator";
import { useDebounce } from "../../../shared/hooks/use-debounce";
import ApiAutocompleteFilter from "../../../features/api/components/api-autocomplete-filter";
import {
	ListResponseClassificationProps,
	ResponseCategory,
	ResponseClassificationProps,
	ResponseStatus,
	activeLabel,
	categoryLabel,
	statusColor,
	statusLabel,
} from "../../../features/product/types/response-classification";
import { ToggleRule } from "./ToggleRule";

const COLUMN_MIN_WIDTH = 155;

const COLUMNS: { label: string; minWidth: number; align?: "center" }[] = [
	{ label: "API", minWidth: COLUMN_MIN_WIDTH },
	{ label: "Retorno", minWidth: 220 },
	{ label: "Categoria", minWidth: COLUMN_MIN_WIDTH },
	{ label: "Cor de alerta", minWidth: COLUMN_MIN_WIDTH },
	{ label: "Ativo", minWidth: 110, align: "center" },
	{ label: "Modificado em", minWidth: COLUMN_MIN_WIDTH },
	{ label: "Modificado por", minWidth: 220 },
];

const TABLE_MIN_WIDTH = COLUMNS.reduce(
	(total, column) => total + column.minWidth,
	0,
);

const CATEGORY_FILTER_OPTIONS: ResponseCategory[] = [
	"restriction",
	"observation",
	"debit",
];

const COLOR_FILTER_OPTIONS: ResponseStatus[] = [
	"green",
	"yellow",
	"red",
	"gray",
];

type ActiveFilter = "" | "true" | "false";

const parseActiveFilter = (value: string | null): ActiveFilter =>
	value === "true" || value === "false" ? value : "";

const parseCategoryFilter = (value: string | null): "" | ResponseCategory =>
	CATEGORY_FILTER_OPTIONS.find((category) => category === value) ?? "";

const parseColorFilter = (value: string | null): "" | ResponseStatus =>
	COLOR_FILTER_OPTIONS.find((status) => status === value) ?? "";

function AlertColorDot({
	status,
	testId,
}: {
	status: ResponseStatus;
	testId?: string;
}) {
	return (
		<Box
			data-testid={testId}
			aria-hidden="true"
			sx={{
				width: 12,
				height: 12,
				borderRadius: "50%",
				flexShrink: 0,
				backgroundColor: statusColor[status],
			}}
		/>
	);
}

const headerCellSx = {
	color: "#6B7280",
	background: "#F9FAFB",
	fontWeight: 600,
};

export default function ResponseClassificationTab(): JSX.Element {
	const [searchParams, setSearchParams] = useSearchParams();
	const [rules, setRules] = useState<ResponseClassificationProps[]>([]);
	const [count, setCount] = useState<number>(0);
	const [pageNumber, setPageNumber] = useState<number>(1);
	const [loading, setLoading] = useState<boolean>(true);
	const [hasError, setHasError] = useState<boolean>(false);
	const searchFilter = searchParams.get("search") ?? "";
	const apiFilter = searchParams.get("api") ?? "";
	const activeFilter = parseActiveFilter(searchParams.get("is_active"));
	const categoryFilter = parseCategoryFilter(searchParams.get("category"));
	const colorFilter = parseColorFilter(searchParams.get("status"));
	const [searchValue, setSearchValue] = useState<string>(searchFilter);
	const debouncedSearch = useDebounce(searchValue.trim());
	const requestIdRef = useRef(0);
	const [exporting, setExporting] = useState<boolean>(false);

	const hasFilters =
		searchFilter !== "" ||
		apiFilter !== "" ||
		activeFilter !== "" ||
		categoryFilter !== "" ||
		colorFilter !== "";

	const buildFilterParams = () => {
		const params = new URLSearchParams();
		if (searchFilter) params.set("search", searchFilter);
		if (apiFilter) params.set("api", apiFilter);
		if (activeFilter) params.set("is_active", activeFilter);
		if (categoryFilter) params.set("category", categoryFilter);
		if (colorFilter) params.set("status", colorFilter);
		return params;
	};

	const buildListUrl = (page: number) => {
		const params = new URLSearchParams({ page: String(page) });
		buildFilterParams().forEach((value, key) => params.set(key, value));
		return `report/response-classification/list/?${params.toString()}`;
	};

	const getRules = async (page: number) => {
		const requestId = ++requestIdRef.current;
		setLoading(true);
		setHasError(false);
		try {
			const response = (await api.get(
				buildListUrl(page),
			)) as ListResponseClassificationProps;
			if (requestId !== requestIdRef.current) return;
			if (!response?.content?.results) {
				setRules([]);
				setHasError(true);
			} else {
				setRules(response.content.results);
				setCount(response.content.count);
			}
		} catch {
			if (requestId !== requestIdRef.current) return;
			setRules([]);
			setHasError(true);
		}
		setLoading(false);
	};

	const refreshRules = async (page: number) => {
		const requestId = ++requestIdRef.current;
		try {
			const response = (await api.get(
				buildListUrl(page),
			)) as ListResponseClassificationProps;
			if (requestId !== requestIdRef.current) return;
			const results = response?.content?.results;
			if (page > 1 && !results?.length) {
				setPageNumber(page - 1);
				refreshRules(page - 1);
				return;
			}
			if (results) {
				setRules(results);
				setCount(response.content.count);
			}
		} catch {
			return;
		}
	};

	const handleExport = async () => {
		setExporting(true);
		try {
			const file = await exportResponseClassifications(buildFilterParams());
			const href = URL.createObjectURL(file);
			const link = document.createElement("a");
			link.href = href;
			link.setAttribute(
				"download",
				`parametrizacoes_${dayjs().format("YYYY-MM-DD")}.xlsx`,
			);
			document.body.appendChild(link);
			link.click();
			document.body.removeChild(link);
			URL.revokeObjectURL(href);
		} catch {
			toast.error("Não foi possível gerar o Excel. Tente novamente.", {
				position: toast.POSITION.TOP_RIGHT,
			});
		}
		setExporting(false);
	};

	const handleRuleToggled = (ruleId: string, isActive: boolean) => {
		setRules((prev) =>
			prev.map((rule) =>
				rule.id === ruleId ? { ...rule, is_active: isActive } : rule,
			),
		);
		refreshRules(pageNumber);
	};

	const handleChangePage = (
		_event: React.ChangeEvent<unknown>,
		value: number,
	) => {
		setPageNumber(value);
		getRules(value);
	};

	const setFilter = (key: string, value: string) => {
		setSearchParams(
			(prev) => {
				const next = new URLSearchParams(prev);
				if (value) {
					next.set(key, value);
				} else {
					next.delete(key);
				}
				return next;
			},
			{ replace: true },
		);
	};

	useEffect(() => {
		if (debouncedSearch !== searchFilter) setFilter("search", debouncedSearch);
	}, [debouncedSearch]);

	useEffect(() => {
		setPageNumber(1);
		getRules(1);
	}, [searchFilter, apiFilter, activeFilter, categoryFilter, colorFilter]);

	const renderFeedbackRow = (content: React.ReactNode) => (
		<TableRow>
			<TableCell colSpan={COLUMNS.length} align="center">
				{content}
			</TableCell>
		</TableRow>
	);

	return (
		<>
			<Box
				sx={{
					display: "flex",
					flexWrap: "wrap",
					gap: 2,
					alignItems: "center",
					my: 2,
				}}
			>
				<FormControl variant="standard" sx={{ width: { xs: "100%", sm: 400 } }}>
					<InputLabel htmlFor="response-classification-search">
						Pesquisar
					</InputLabel>
					<Input
						id="response-classification-search"
						placeholder="Retorno ou texto personalizado"
						value={searchValue}
						onChange={(e) => setSearchValue(e.target.value)}
						startAdornment={
							<InputAdornment position="start">
								<SearchIcon fontSize="small" />
							</InputAdornment>
						}
					/>
				</FormControl>
				<ApiAutocompleteFilter
					value={apiFilter}
					onChange={(apiId) => setFilter("api", apiId)}
				/>
				<FormControl variant="outlined" size="small" sx={{ minWidth: 140 }}>
					<InputLabel id="response-classification-active-label" shrink>
						Ativo
					</InputLabel>
					<Select
						labelId="response-classification-active-label"
						label="Ativo"
						value={activeFilter}
						displayEmpty
						notched
						onChange={(e) => setFilter("is_active", e.target.value)}
						renderValue={(value) =>
							value ? activeLabel(value === "true") : "Todos"
						}
					>
						<MenuItem value="">Todos</MenuItem>
						<MenuItem value="true">{activeLabel(true)}</MenuItem>
						<MenuItem value="false">{activeLabel(false)}</MenuItem>
					</Select>
				</FormControl>
				<FormControl variant="outlined" size="small" sx={{ minWidth: 180 }}>
					<InputLabel id="response-classification-category-label" shrink>
						Categoria
					</InputLabel>
					<Select
						labelId="response-classification-category-label"
						label="Categoria"
						value={categoryFilter}
						displayEmpty
						notched
						onChange={(e) => setFilter("category", e.target.value)}
						renderValue={(value) => (value ? categoryLabel[value] : "Todas")}
					>
						<MenuItem value="">Todas</MenuItem>
						{CATEGORY_FILTER_OPTIONS.map((category) => (
							<MenuItem key={category} value={category}>
								{categoryLabel[category]}
							</MenuItem>
						))}
					</Select>
				</FormControl>
				<FormControl variant="outlined" size="small" sx={{ minWidth: 180 }}>
					<InputLabel id="response-classification-color-label" shrink>
						Cor de alerta
					</InputLabel>
					<Select
						labelId="response-classification-color-label"
						label="Cor de alerta"
						value={colorFilter}
						displayEmpty
						notched
						onChange={(e) => setFilter("status", e.target.value)}
						renderValue={(value) =>
							value ? (
								<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
									<AlertColorDot status={value} />
									{statusLabel[value]}
								</Box>
							) : (
								"Todas"
							)
						}
					>
						<MenuItem value="">Todas</MenuItem>
						{COLOR_FILTER_OPTIONS.map((status) => (
							<MenuItem key={status} value={status} sx={{ gap: 1 }}>
								<AlertColorDot status={status} />
								{statusLabel[status]}
							</MenuItem>
						))}
					</Select>
				</FormControl>
				<Button
					variant="outlined"
					color="success"
					sx={{ ml: { sm: "auto" }, minWidth: 130 }}
					onClick={handleExport}
					disabled={exporting}
				>
					{exporting ? "Gerando..." : "Gerar Excel"}
				</Button>
			</Box>
			<TableContainer sx={{ overflowX: "auto" }}>
				<Table
					size="small"
					aria-label="parametrizações de retorno"
					sx={{ minWidth: TABLE_MIN_WIDTH }}
				>
					<TableHead>
						<TableRow>
							{COLUMNS.map((column) => (
								<TableCell
									key={column.label}
									align={column.align}
									sx={{
										...headerCellSx,
										minWidth: column.minWidth,
										width: column.minWidth,
									}}
								>
									{column.label}
								</TableCell>
							))}
						</TableRow>
					</TableHead>
					<TableBody>
						{loading
							? renderFeedbackRow(<CircularProgress size={24} />)
							: hasError
								? renderFeedbackRow(
										"Não foi possível carregar as parametrizações.",
									)
								: rules.length === 0
									? renderFeedbackRow(
											hasFilters
												? "Nenhuma parametrização encontrada para os filtros aplicados."
												: "Nenhuma parametrização cadastrada",
										)
									: rules.map((rule) => (
											<TableRow key={rule.id} hover={rule.is_active}>
												<TableCell
													sx={{
														":hover": {
															textDecoration: rule.is_active
																? "underline"
																: "none",
														},
													}}
												>
													{rule.is_active ? (
														<Link to={`/response-classification/${rule.id}`}>
															{rule.api_name}
														</Link>
													) : (
														<span>{rule.api_name}</span>
													)}
												</TableCell>
												<TableCell>
													<Box
														component="span"
														sx={{ color: "text.secondary" }}
													>
														{rule.key}
													</Box>
													{rule.label && (
														<>
															<Box
																component="span"
																sx={{ color: "text.secondary" }}
															>
																{" "}
																-{" "}
															</Box>
															<span>{rule.label}</span>
														</>
													)}
												</TableCell>
												<TableCell>{categoryLabel[rule.category]}</TableCell>
												<TableCell>
													<Box
														sx={{
															display: "flex",
															alignItems: "center",
															gap: 1,
														}}
													>
														<AlertColorDot
															status={rule.status}
															testId="alert-color-dot"
														/>
														{statusLabel[rule.status]}
													</Box>
												</TableCell>
												<TableCell align="center">
													<ToggleRule
														rule={rule}
														onToggled={(isActive) =>
															handleRuleToggled(rule.id, isActive)
														}
													/>
												</TableCell>
												<TableCell sx={{ whiteSpace: "nowrap" }}>
													<Box
														component="span"
														sx={{ color: "text.secondary" }}
													>
														{rule.updated_at}
													</Box>
												</TableCell>
												<TableCell>{rule.updated_by_email || "—"}</TableCell>
											</TableRow>
										))}
					</TableBody>
				</Table>
			</TableContainer>
			{!hasError && count > 0 && (
				<Paginator
					count={count}
					pageNumber={pageNumber}
					onChange={handleChangePage}
				/>
			)}
		</>
	);
}
